import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ToolActivity from './ToolActivity';
import downloadReportAsExcel from './excel';
import { Citation, CopilotMessage as Msg } from './types';

interface Props {
  message: Msg;
  onCitation: (path: string) => void;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The real "Download Excel" button is rendered by the app from message.report. The model
// sometimes also writes its own download link/URL into the reply text (with a placeholder or
// empty href) — a dead, duplicate link. Strip any markdown link whose visible text mentions
// "download", and any bare download-URL, so only the real button remains.
const stripDownloadLinks = (content: string): string => content
  .replace(/\[([^\]]*\bdownload\b[^\]]*)\]\([^)]*\)/gi, '$1') // keep text, drop the (dead) link
  .replace(/^\s*download excel.*$/gim, '') // drop a stray "Download Excel" line the model added
  .replace(/\n{3,}/g, '\n\n')
  .trim();

// Turn workflow UIDs mentioned in the reply (e.g. in a table) into markdown links,
// using the UID→route map already present in the message's citations.
const linkifyUids = (content: string, citations?: Citation[]): string => {
  if (!content || !citations || citations.length === 0) return content;
  const map: Record<string, string> = {};
  citations.forEach((c) => {
    const m = c.label.match(/LMR-\d+/i);
    if (!m || !c.path) return;
    const uid = m[0];
    // Prefer the workflow-detail route over an audit route for inline links.
    if (!map[uid] || c.path.includes('/workflow/edit/')) map[uid] = c.path;
  });
  const uids = Object.keys(map).sort((a, b) => b.length - a.length);
  if (uids.length === 0) return content;
  const re = new RegExp(`\\b(${uids.map(escapeRe).join('|')})\\b`, 'g');
  // Avoid double-linking anything already inside a markdown link.
  return content.replace(re, (match, _g, offset: number, full: string) => (
    full[offset - 1] === '[' ? match : `[${match}](${map[match]})`
  ));
};

const CopilotMessage = ({ message, onCitation }: Props) => {
  const isUser = message.role === 'user';
  const rowCls = `copilot-row ${isUser ? 'is-user' : 'is-assistant'}`;
  const bubbleCls = `copilot-bubble${message.isError ? ' is-error' : ''}`;
  const content = isUser
    ? message.content
    : linkifyUids(stripDownloadLinks(message.content), message.citations);

  // Custom link renderer: in-app routes navigate via onCitation; external links open normally.
  const mdComponents = {
    a: ({ href, children }: any) => (
      href && href.startsWith('/') ? (
        <a
          href={href}
          onClick={(e) => { e.preventDefault(); onCitation(href); }}
        >
          {children}
        </a>
      ) : (
        <a href={href} target="_blank" rel="noreferrer">{children}</a>
      )
    ),
  };

  return (
    <div className={rowCls}>
      {!isUser && (
        <div className="copilot-avatar">
          <i className="pi pi-sparkles" />
        </div>
      )}

      <div className="copilot-bubble-wrap">
        <div className={bubbleCls}>
          {isUser ? (
            <div className="copilot-msg-text">{message.content}</div>
          ) : (
            <div className="copilot-msg-text copilot-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {!isUser && !message.isError && message.content && (
          <button
            type="button"
            className="copilot-copy"
            title="Copy answer"
            onClick={() => navigator.clipboard?.writeText(message.content)}
          >
            <i className="pi pi-copy" />
            Copy
          </button>
        )}

        {!isUser && message.report && message.report.rows.length > 0 && (
          <button
            type="button"
            className="copilot-download"
            onClick={() => message.report && downloadReportAsExcel(message.report)}
          >
            <i className="pi pi-file-excel" />
            {`Download Excel (${message.report.rows.length} rows)`}
          </button>
        )}

        {!isUser && <ToolActivity items={message.toolActivity} />}

        {!isUser && !message.report && message.citations && message.citations.length > 0 && (
          <div className="copilot-citations">
            {message.citations.map((c: Citation, i: number) => (
              <button
                type="button"
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className="copilot-citation"
                disabled={!c.path}
                onClick={() => c.path && onCitation(c.path)}
              >
                <i className="pi pi-external-link" />
                {' '}
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CopilotMessage;
